
import React, { useEffect, useState, useCallback } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useSelector } from 'react-redux'
import jsPDF from 'jspdf'
import 'jspdf-autotable'
import axios from 'axios'
import toasters from '../../../../../../../utils/toasters/Toasters'
import { getAllDeviceHighScoreList } from '../../../../../../../utils/api/deviceHighScoreAPI'
import APIErrorComponent from '../../../../../../components/_shared/Error/APIErrorComponent'
import DeviceHighScoringTable from '../../../devicesHighScoring/DevicesHighScoringListing/DevicesHighScoringTable/DevicesHighScoringTable'
import { deviceHighScoringColumn } from '../../../devicesHighScoring/DevicesHighScoringListing/DevicesHighScoringTable/devicesHighScoringColumn'

import { CSVLink } from 'react-csv'

import { Button } from 'reactstrap'
import { validatePermission } from '../../../../../../../utils/services/validatePermission'


const buildCSVData = (rows, columns) => {
    const header = columns.map(col => col.name)

    const body = rows.map(row =>
        columns.map(col =>
            col.selector ? col.selector(row) ?? '' : ''
        )
    )

    return [header, ...body]
}


const formatPayloadMonth = (payload) => {
  if (!payload) return payload;
  return { start_dt: payload.month };
};

const DeviceHighScoringReport = () => {
  const location = useLocation();
  const { permissions } = useSelector((state) => state.auth);
  const [searchParams] = useSearchParams();

  // Payload initialization: location.state → sessionStorage → URL params
  const [payload, setPayload] = useState(() => {
    if (location.state) return location.state;

    const stored = sessionStorage.getItem("deviceHighScorePayload");
    if (stored) return JSON.parse(stored);

    const params = Object.fromEntries([...searchParams]);
    return Object.keys(params).length ? params : null;
  });

  const [formattedPayload, setFormattedPayload] = useState(() =>
    payload ? formatPayloadMonth(payload) : null
  );

  // Sync payload with sessionStorage and URL params
  useEffect(() => {
    if (location.state) {
      const prevStored = sessionStorage.getItem("deviceHighScorePayload");
      const prev = prevStored ? JSON.parse(prevStored) : null;

      if (JSON.stringify(prev) !== JSON.stringify(location.state)) {
        sessionStorage.setItem(
          "deviceHighScorePayload",
          JSON.stringify(location.state)
        );
        setPayload(location.state);
        setFormattedPayload(formatPayloadMonth(location.state));
      }
    } else if (!payload) {
      // fallback to URL query params
      const params = Object.fromEntries([...searchParams]);
      if (Object.keys(params).length) {
        sessionStorage.setItem("deviceHighScorePayload", JSON.stringify(params));
        setPayload(params);
        setFormattedPayload(formatPayloadMonth(params));
      }
    }
  }, [location.state, searchParams]);

  // Table options & sort from sessionStorage
  const storedTableOptions =
    JSON.parse(sessionStorage.getItem("deviceHighScoreTableOptions")) || {
      rowPerPages: 10,
      activePage: 0,
    };
  const storedSort =
    JSON.parse(sessionStorage.getItem("deviceHighScoreSort")) || null;

  const [tableOptions, setTableOptions] = useState(storedTableOptions);
  const [sort, setSort] = useState(storedSort);

  useEffect(() => {
    sessionStorage.setItem(
      "deviceHighScoreTableOptions",
      JSON.stringify(tableOptions)
    );
  }, [tableOptions]);

  useEffect(() => {
    sessionStorage.setItem("deviceHighScoreSort", JSON.stringify(sort));
  }, [sort]);

    const getDeviceHighScoreData = useCallback(async () => {
        if (!formattedPayload?.start_dt) return []

        const params = {
            per_page: tableOptions.rowPerPages,
            page: tableOptions.activePage + 1,
            ...formattedPayload,
        }

        if (sort?.columnName && sort.order) {
            params.sort = sort.columnName
            params.order = sort.order
        }

        return await getAllDeviceHighScoreList(params)
    }, [tableOptions, sort, formattedPayload])

    const { isLoading, data, isError, error } = useQuery(
        ['device-high-score', tableOptions, sort, formattedPayload],
        getDeviceHighScoreData,
        {
            enabled: !!formattedPayload?.start_dt,
            keepPreviousData: true,
            retry: 1,
        }
    )

    const handlePageClick = (event) =>
        setTableOptions(prev => ({ ...prev, activePage: event.selected }))

    const handleRowsPerPage = (e) =>
        setTableOptions(prev => ({
            ...prev,
            rowPerPages: Number(e.target.value),
            activePage: 0,
        }))

    const handleColumnSort = (column, direction) =>
        setSort({ columnName: column?.sortField, order: direction })

    const fetchAllForExport = async () => {
        const response = await getAllDeviceHighScoreList({
            ...formattedPayload,
            pagination: 'false',
        })
        return response?.DataRecords || []
    }

    const downloadPDF = async () => {
        try {
            const fullData = await fetchAllForExport()

            const doc = new jsPDF({
                orientation: 'landscape',
                unit: 'mm',
                format: 'a4',
            })

            doc.setFontSize(18)
            doc.text('Device High Scoring Report', 14, 22)
            doc.setFontSize(12)
            doc.text(`Date: ${formattedPayload?.start_dt}`, 14, 30)

            const tableColumn = deviceHighScoringColumn.map(col => col.name)
            const tableRows = fullData.map(item =>
                deviceHighScoringColumn.map(col =>
                    col.selector ? col.selector(item) ?? '' : ''
                )
            )

            doc.autoTable({
                head: [tableColumn],
                body: tableRows,
                startY: 40,
                theme: 'grid',
                styles: { fontSize: 10 },
                headStyles: {
                    fillColor: [0, 81, 242],
                    textColor: [255, 255, 255],
                },
            })

            doc.save('device-high-scoring.pdf')
        } catch (err) {
            console.error(err)
            toasters({ type: 'error', message: 'Error generating PDF' })
        }
    }

    const [exportData, setExportData] = useState([])

   const downloadExcel = async () => {
    try {
        const fullData = await fetchAllForExport()
        const csvData = buildCSVData(fullData, deviceHighScoringColumn)

        const csvContent = csvData
            .map(row =>
                row.map(value => `"${String(value).replace(/"/g, '""')}"`).join(',')
            )
            .join('\n')

        const blob = new Blob([csvContent], {
            type: 'text/csv;charset=utf-8;',
        })

        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')

        link.href = url
        link.setAttribute(
            'download',
            `Device-High-Scoring-${formattedPayload?.start_dt}.csv`
        )

        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
    } catch (err) {
        console.error(err)
        toasters({ type: 'error', message: 'Excel export failed' })
    }
}


    const pageCount = Math.ceil(
        (data?.totalCount || 0) / tableOptions.rowPerPages
    )

    return (
        <>
            {isError ? (
                <APIErrorComponent error={error} />
            ) : (
                <>
                  

                    <DeviceHighScoringTable
                        isLoading={isLoading}
                        data={data?.DataRecords || []}
                        totaldata={data?.totalCount}
                        tableOptions={tableOptions}
                        handlePageClick={handlePageClick}
                        handleRowsPerPage={handleRowsPerPage}
                        handleColumnSort={handleColumnSort}
                        downloadPDF={downloadPDF}
                        pageCount={pageCount}
                        DisplayDate={formattedPayload}
                        reportCode={payload?.code || 'DeviceHighScore'}
                        reportTableData={data?.DataRecords}
                         excelMode="client"
                          downloadExcel={downloadExcel} 
                    />
                </>
            )}
        </>
    )
}


export default DeviceHighScoringReport